import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../assets/img/logoblue.png';
import { gsap } from 'gsap';
import '../index.css';
import './nav.css';
import earthGlobe from '../assets/img/Animation-1730316564384.json';
import Nav from './Nav';

export const Header = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [linksHidden, setLinksHidden] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [buttonText, setButtonText] = useState('Contact');
  const buttonRef = useRef(null);
  const buttonTextRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
      if (window.scrollY > 100) {
        setLinksHidden(true);
      } else {
        setLinksHidden(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getHidePercentage = () => {
    const maxScroll = 150;
    return Math.min(scrollPosition / maxScroll * 100, 100);
  };

  useEffect(() => {
    const hidePercentage = getHidePercentage();
    const navItems = document.querySelectorAll('.nav-text');
    const linkContainers = document.querySelectorAll('.nav-link-container');

    // Animate nav items on scroll
    navItems.forEach((item) => {
      gsap.to(item, {
        y: linksHidden ? '-100%' : '0%',
        opacity: linksHidden ? 0 : 1,
        duration: 0.6,
        ease: 'power3.out',
      });
    });

    // Add clip-path effect for link containers
    linkContainers.forEach((container) => {
      gsap.to(container, {
        clipPath: linksHidden
          ? 'inset(0 0 0 0)'
          : 'inset(10% 0 0 0)',
        opacity: linksHidden ? 0 : 1,
        duration: 0.6,
        ease: 'power2.out',
      });
    });

    // Button animation for desktop and mobile
    if (hidePercentage === 100) {
      if (buttonText !== 'Menu') {
        gsap.fromTo(
          buttonTextRef.current,
          { y: '100%', opacity: 0 },
          { 
            y: '0%', 
            opacity: 1,
            duration: 0.3, 
            ease: 'power2.out', 
            onComplete: () => setButtonText('Menu') 
          }
        );
      }
    } else {
      if (buttonText !== 'Contact') {
        gsap.fromTo(
          buttonTextRef.current,
          { y: '-100%', opacity: 0 },
          { 
            y: '0%', 
            opacity: 1,
            duration: 0.3, 
            ease: 'power2.out', 
            onComplete: () => setButtonText('Contact') 
          }
        );
      }
    }
    // getHidePercentage only reads scrollPosition, which is already a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition, linksHidden, buttonText]);

  const handleReciveOffersClick = () => {
    if (buttonText === 'Menu') {
      setIsNavOpen(!isNavOpen);
    } else if (buttonText === 'Contact') {
      navigate('/contact');
    }
  };

  const handleNavClose = () => {
    setIsNavOpen(false);
  };

  return (
    <header className="py-4 z-50 w-full">
      <div className="header-container lg:px-4 md:px-4 sm:px-2 px-2 mx-auto flex justify-between items-start max-w-full">
        {/* Logo Section */}
        <div className="logo-section flex-shrink-0">
          <Link to="/">
            <img className="Logo_menu w-10 sm:w-12 h-auto z-60 ml-1 sm:ml-2" src={Logo} alt="Logo" />
          </Link>
        </div>

        {/* Global Element - Responsive */}
        <div className="global-section flex-1 flex items-center justify-center text-neutral-300 mx-2 sm:mx-4 lg:ml-16">
          <span className="flex items-center lottie">
            <p className="text-xs sm:text-sm lg:text-base">We re global</p> 
            <Lottie animationData={earthGlobe} className="ml-1 sm:ml-2 w-4 h-4 sm:w-6 sm:h-6" />
          </span>
        </div>

        {/* Desktop Navigation - Show on larger screens */}
        <div className="routes1 hidden md:block lg:block">
          <div className="mr-4 md:mr-8 mt-[8px] flex flex-col items-start text-left text-[1.2rem] md:text-[1.5rem] font-light">
            <div className={`relative group link-hover-slide ${linksHidden ? 'pointer-events-none' : ''}`}>
              <div className="nav-link-container overflow-hidden" style={{ clipPath: 'inset(0 0 0 0)' }}>
                <Link to="/house-list">
                  <p className="font-light text-white nav-text">Listings</p>
                </Link>
              </div>
            </div>
            <div className={`relative group link-hover-slide ${linksHidden ? 'pointer-events-none' : ''}`}>
              <div className="nav-link-container overflow-hidden" style={{ clipPath: 'inset(0 0 0 0)' }}>
                <Link to="/services_page/real-estate-brokerage">
                  <p className="font-light text-white nav-text">Services</p>
                </Link>
              </div>
            </div>
            <div className={`relative group link-hover-slide ${linksHidden ? 'pointer-events-none' : ''}`}>
              <div className="nav-link-container overflow-hidden" style={{ clipPath: 'inset(0 0 0 0)' }}>
                <Link to="/">
                  <p className="font-light text-white nav-text">Main</p>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Button Section - Always Visible on All Screens */}
        <div className="routes2 flex-shrink-0 block">
          <div className="mt-[6px] text-left">
            <button
              ref={buttonRef}
              className="Recive-offers-button flex justify-center items-center text-white rounded-full 
              transition duration-500 border border-indigo-700 overflow-hidden group"
              onClick={handleReciveOffersClick}
            >
              <span className="absolute inset-0 bg-indigo-700 transition-transform duration-500 transform group-hover:-translate-x-full"></span>
              <p 
                className="text-white relative z-10 w-full text-center flex justify-center items-center" 
                ref={buttonTextRef}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {buttonText}
              </p>
            </button>
          </div>
        </div>
      </div>
      {isNavOpen && <Nav isOpen={isNavOpen} onClose={handleNavClose} />}
    </header>
  );
};

export default Header;